import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface EmptyStateProps {
  icon: LucideIcon
  title: string
  /** One or two sentences: what would be here, and what the next step is. */
  description: string
  /** The action that fills the emptiness, when there is exactly one. */
  action?: ReactNode
  className?: string
}

/**
 * The shared shape for "there is nothing here yet".
 *
 * Before this, every page invented its own — some a warning-coloured alert, some a bare
 * sentence. An empty list is not an error, so it must not look like one; it is a place where
 * something is missing, and the useful thing to show is what would go there.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'border-border flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center',
        className,
      )}
    >
      <div className="bg-muted text-muted-foreground rounded-full p-3">
        <Icon className="size-5" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-muted-foreground mx-auto max-w-prose text-sm">{description}</p>
      </div>
      {action}
    </div>
  )
}
